import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import { Edit as EditIcon, ToggleOn, ToggleOff } from '@mui/icons-material';
import {
  getClientPartners,
  createClientPartner,
  updateClientPartner,
  getClientPartnersByWarehouse,
  getClientPartnersByStatus,
  getClientPartnersByClientId
} from '../services/fundedhereService';
import ErrorDisplay from '../../components/ErrorDisplay';
import { sanitizeInput } from '../../utils/validationUtils';

const ClientsPartner = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  // Filter state
  const [warehouseIdFilter, setWarehouseIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientIdFilter, setClientIdFilter] = useState('');
  const [isFilterActive, setIsFilterActive] = useState(false);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentPartner, setCurrentPartner] = useState({
    id: null,
    clientId: '',
    warehouseId: '',
    email: '',
    status: 1
  });



  const fetchPartners = async (currentPage, pageSize) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getClientPartners({ page: currentPage, size: pageSize });

      // Handle response data based on its structure
      let partnersData = [];
      let totalElementsCount = 0;

      if (Array.isArray(response)) {
        // If response is directly an array (rare but possible)
        partnersData = response;
        totalElementsCount = response.length;
      } else if (Array.isArray(response.data)) {
        // If response.data is an array
        partnersData = response.data;
        totalElementsCount = response.data.length;
      } else if (response.data && response.data.data) {
        // If response.data has a nested data property
        partnersData = response.data.data || [];
        totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.data.length || 0;
      } else if (response.data && response.data.content) {
        // If response.data has a content property (common in Spring Data)
        partnersData = response.data.content || [];
        totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.content.length || 0;
      } else if (response.data && typeof response.data === 'object') {
        // If response.data is an object that might contain the array under a different property
        // Try common properties like 'content', 'items', 'results', etc.
        if (response.data.content && Array.isArray(response.data.content)) {
          partnersData = response.data.content;
          totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.content.length || 0;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          partnersData = response.data.items;
          totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.items.length || 0;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          partnersData = response.data.results;
          totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.results.length || 0;
        } else {
          // If none of the above patterns match, try to get any array property from the data
          const dataKeys = Object.keys(response.data);
          for (const key of dataKeys) {
            if (Array.isArray(response.data[key])) {
              partnersData = response.data[key];
              totalElementsCount = response.data.totalElements || response.data.totalElements || response.data[key].length || 0;
              break;
            }
          }
          if (partnersData.length === 0 && Array.isArray(response.data)) {
            // If the entire response.data is an array but wasn't caught by the first condition
            partnersData = response.data;
            totalElementsCount = response.data.length;
          }
        }
      } else {
        // Fallback: try to use response.data directly if it's an array
        partnersData = Array.isArray(response) ? response : [];
        totalElementsCount = partnersData.length;
      }

      setPartners(partnersData);
      setTotalElements(totalElementsCount);
      setIsFilterActive(false);
    } catch (err) {
      console.error('Error fetching client partners:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred while fetching data.');
      setPartners([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleApplyFilter = async () => {
    setLoading(true);
    setError(null);
    setIsFilterActive(true);
    try {
      let response;
      if (clientIdFilter) { // Check client ID filter first
        response = await getClientPartnersByClientId(clientIdFilter);
      } else if (warehouseIdFilter) {
        response = await getClientPartnersByWarehouse(warehouseIdFilter);
      } else if (statusFilter !== '') {
        response = await getClientPartnersByStatus(statusFilter);
      } else {
        fetchPartners(page, rowsPerPage);
        return;
      }
      // Handle response data based on its structure for filtered results
      let partnersData = [];
      let totalElementsCount = 0;

      if (Array.isArray(response)) {
        // If response is directly an array (rare but possible)
        partnersData = response;
        totalElementsCount = response.length;
      } else if (Array.isArray(response.data)) {
        // If response.data is an array
        partnersData = response.data;
        totalElementsCount = response.data.length;
      } else if (response.data && response.data.data) {
        // If response.data has a nested data property
        partnersData = response.data.data || [];
        totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.data.length || 0;
      } else if (response.data && response.data.content) {
        // If response.data has a content property (common in Spring Data)
        partnersData = response.data.content || [];
        totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.content.length || 0;
      } else if (response.data && typeof response.data === 'object') {
        // If response.data is an object that might contain the array under a different property
        // Try common properties like 'content', 'items', 'results', etc.
        if (response.data.content && Array.isArray(response.data.content)) {
          partnersData = response.data.content;
          totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.content.length || 0;
        } else if (response.data.items && Array.isArray(response.data.items)) {
          partnersData = response.data.items;
          totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.items.length || 0;
        } else if (response.data.results && Array.isArray(response.data.results)) {
          partnersData = response.data.results;
          totalElementsCount = response.data.totalElements || response.data.totalElements || response.data.results.length || 0;
        } else {
          // If none of the above patterns match, try to get any array property from the data
          const dataKeys = Object.keys(response.data);
          for (const key of dataKeys) {
            if (Array.isArray(response.data[key])) {
              partnersData = response.data[key];
              totalElementsCount = response.data.totalElements || response.data.totalElements || response.data[key].length || 0;
              break;
            }
          }
          if (partnersData.length === 0 && Array.isArray(response.data)) {
            // If the entire response.data is an array but wasn't caught by the first condition
            partnersData = response.data;
            totalElementsCount = response.data.length;
          }
        }
      } else {
        // Fallback: try to use response.data directly if it's an array
        partnersData = Array.isArray(response) ? response : [];
        totalElementsCount = partnersData.length;
      }

      setPartners(partnersData);
      setTotalElements(totalElementsCount);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch filtered data.');
      setPartners([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilter = () => {
    setWarehouseIdFilter('');
    setStatusFilter('');
    setClientIdFilter('');
    setIsFilterActive(false);
    fetchPartners(0, rowsPerPage);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (partner = null) => {
    if (partner) {
      setIsEditing(true);
      setCurrentPartner(partner);
    } else {
      setIsEditing(false);
      setCurrentPartner({ id: null, clientId: '', warehouseId: '', email: '', status: 1 });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentPartner(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (isEditing) {
        await updateClientPartner(currentPartner.id, currentPartner);
      } else {
        await createClientPartner(currentPartner);
      }
      handleCloseDialog();
      handleClearFilter(); // Refresh data
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save partner.');
    }
  };

  const handleToggleStatus = async (partner) => {
    const newStatus = partner.status === 1 ? 0 : 1;
    const updatedPartner = { ...partner, status: newStatus };

    try {
      // Optimistically update the UI
      setPartners(prevPartners => 
        prevPartners.map(p => 
          p.id === partner.id ? updatedPartner : p
        )
      );
      
      await updateClientPartner(partner.id, updatedPartner);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status.');
      // Revert the UI change if the API call fails
      setPartners(prevPartners => 
        prevPartners.map(p => 
          p.id === partner.id ? partner : p
        )
      );
    }
  };


  return (
    <Box>
      <ErrorDisplay error={error} onClose={() => setError(null)} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Clients Partner Management
        </Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenDialog()}>
          Create Client
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filters</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Client ID"
              variant="outlined"
              value={sanitizeInput(clientIdFilter)}
              onChange={(e) => {
                setClientIdFilter(e.target.value);
                if (warehouseIdFilter) setWarehouseIdFilter('');
                if (statusFilter) setStatusFilter('');
              }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              label="Warehouse ID"
              variant="outlined"
              value={sanitizeInput(warehouseIdFilter)}
              onChange={(e) => {
                setWarehouseIdFilter(e.target.value);
                if (clientIdFilter) setClientIdFilter('');
                if (statusFilter) setStatusFilter('');
              }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  if (clientIdFilter) setClientIdFilter('');
                  if (warehouseIdFilter) setWarehouseIdFilter('');
                }}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button variant="contained" onClick={handleApplyFilter}>Apply</Button>
          <Button variant="outlined" onClick={handleClearFilter}>Clear</Button>
        </Box>
      </Paper>
      
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Client ID</TableCell>
                <TableCell>Warehouse ID</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center"><CircularProgress /></TableCell>
                </TableRow>
              ) : (
                partners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell>{sanitizeInput(partner.id)}</TableCell>
                    <TableCell>{sanitizeInput(partner.clientId)}</TableCell>
                    <TableCell>{sanitizeInput(partner.warehouseId)}</TableCell>
                    <TableCell>{sanitizeInput(partner.email)}</TableCell>
                    <TableCell>
                      <Tooltip title={partner.status === 1 ? 'Deactivate Client' : 'Activate Client'}>
                        <IconButton onClick={() => handleToggleStatus(partner)} size="large" aria-label="toggle status">
                          {partner.status === 1 ? <ToggleOn color="success" fontSize="large" /> : <ToggleOff fontSize="large" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{new Date(partner.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Tooltip title="Edit Client Partner">
                        <IconButton onClick={() => handleOpenDialog(partner)} size="small" aria-label="edit client">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!isFilterActive && (
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Dialog for adding/editing */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Client Partner' : 'Create New Client Partner'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="clientId"
            label="Client ID"
            type="text"
            fullWidth
            variant="outlined"
            value={sanitizeInput(currentPartner.clientId)}
            onChange={handleInputChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="warehouseId"
            label="Warehouse ID"
            type="text"
            fullWidth
            variant="outlined"
            value={sanitizeInput(currentPartner.warehouseId)}
            onChange={handleInputChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={sanitizeInput(currentPartner.email)}
            onChange={handleInputChange}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsPartner;